import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, X, ArrowUpRight, Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin,
  ChevronDown, ChevronRight, Video, Radio, Sparkles, Camera, Share2, Palette, Code, Layers
} from 'lucide-react';
import whatsappIcon from '../../assets/WhatsappICON.png';
import logoWhite from '../../assets/WE24-Logo - BEYAZ-Crop.png';
import { useLanguage } from '../../context/LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { services as servicesTr } from '../../data/services';
import { servicesEn } from '../../data/services.en';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesMegaOpen, setIsServicesMegaOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const megaMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, getLocalizedPath } = useLanguage();

  const currentServices = language === 'TR' ? servicesTr : servicesEn;

  const handleLanguageSwitch = (targetLang: 'TR' | 'EN') => {
    if (language === targetLang) return;
    setLanguage(targetLang);
    const targetPath = getLocalizedPath(location.pathname, targetLang);
    navigate(targetPath);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle Escape key to close mobile menu and return focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setIsServicesMegaOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  const handleMegaMouseEnter = () => {
    if (megaMenuTimeoutRef.current) clearTimeout(megaMenuTimeoutRef.current);
    setIsServicesMegaOpen(true);
  };

  const handleMegaMouseLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setIsServicesMegaOpen(false);
    }, 200);
  };

  const navLinks = language === 'TR' ? [
    { name: "Ana Sayfa", path: "/" },
    { name: "Projeler", path: "/projeler" },
    { name: "Hizmetlerimiz", path: "/hizmetler", hasDropdown: true },
    { name: "İçgörüler", path: "/icgoruler" },
    { name: "Hakkımızda", path: "/hakkimizda" },
    { name: "Markalar", path: "/markalar" },
    { name: "İletişim", path: "/iletisim" },
  ] : [
    { name: "Home", path: "/en" },
    { name: "Projects", path: "/en/projects" },
    { name: "Services", path: "/en/services", hasDropdown: true },
    { name: "Insights", path: "/en/insights" },
    { name: "About Us", path: "/en/about" },
    { name: "Brands", path: "/en/brands" },
    { name: "Contact", path: "/en/contact" },
  ];

  const socialLinks = [
    { icon: <Instagram className="w-3 h-3" />, url: "https://www.instagram.com/we24_agency/", label: "Instagram" },
    { icon: <Facebook className="w-3 h-3" />, url: "https://www.facebook.com/we24agency/", label: "Facebook" },
    { icon: <Youtube className="w-3 h-3" />, url: "https://www.youtube.com/@WE24agency", label: "YouTube" },
    { icon: <Linkedin className="w-3 h-3" />, url: "https://www.linkedin.com/company/67730275/", label: "LinkedIn" },
    { icon: <MapPin className="w-3 h-3 text-[#ff7a63]" />, url: "https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul", label: "Google Haritalar" },
  ];

  const renderServiceIcon = (iconName: string) => {
    switch (iconName) {
      case 'Video': return <Video className="w-4 h-4 text-[#ff7a63]" />;
      case 'Radio': return <Radio className="w-4 h-4 text-[#ff7a63]" />;
      case 'Sparkles': return <Sparkles className="w-4 h-4 text-[#ff7a63]" />;
      case 'Camera': return <Camera className="w-4 h-4 text-[#ff7a63]" />;
      case 'Share2': return <Share2 className="w-4 h-4 text-[#ff7a63]" />;
      case 'Palette': return <Palette className="w-4 h-4 text-[#ff7a63]" />;
      case 'Code': return <Code className="w-4 h-4 text-[#ff7a63]" />;
      default: return <Layers className="w-4 h-4 text-[#ff7a63]" />;
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Top Bar (Menü Üstü Alanı) */}
      <div className="border-b border-white/[0.08] bg-[#071927]/95 backdrop-blur-md text-xs py-1.5 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Sol Kısım: Telefon, Adres & Sosyal Medya İkonları */}
          <div className="flex items-center space-x-3 sm:space-x-5 text-zinc-300 font-medium">
            <a
              href="tel:+905452292424"
              onClick={() => trackEvent('phone_click', { placement: 'navbar_top' })}
              className="flex items-center space-x-2 hover:text-white transition-colors group"
              title="Telefon ile ara"
            >
              <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Phone className="w-3 h-3 text-[#ff7a63] group-hover:scale-110 transition-transform" />
              </div>
              <span className="tracking-wide text-[11px] sm:text-xs font-semibold text-zinc-200 group-hover:text-white">
                +90 545 229 24 24
              </span>
            </a>

            <span className="text-zinc-700 hidden md:inline">|</span>

            <a
              href="https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center space-x-1.5 text-zinc-400 hover:text-white transition-colors group"
              title="Google Haritalar'da Görüntüle"
            >
              <MapPin className="w-3.5 h-3.5 text-[#ff7a63] group-hover:scale-110 transition-transform shrink-0" />
              <span className="group-hover:underline truncate max-w-[240px] text-[11px] sm:text-xs">
                Nişantaşı, İstanbul
              </span>
            </a>

            <span className="text-zinc-700 hidden md:inline">|</span>

            {/* Sosyal Medya İkonları */}
            <div className="hidden md:flex items-center space-x-2 text-zinc-400">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-5 h-5 rounded-full bg-white/5 hover:bg-white/15 border border-white/5 flex items-center justify-center text-zinc-400 hover:text-white transition-all hover:scale-110"
                  aria-label={social.label}
                  title={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Sağ Kısım: WhatsApp, Email & TR / EN Dil Seçimi */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* WhatsApp Butonu */}
            <a
              href="https://wa.me/905452292424"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('whatsapp_click', { placement: 'navbar_top' })}
              className="flex items-center space-x-1.5 px-2.5 py-1 bg-emerald-950/40 hover:bg-emerald-900/60 border border-emerald-500/30 hover:border-emerald-400/60 rounded-md font-semibold text-zinc-100 transition-all shadow-sm group text-[11px] sm:text-xs"
            >
              <img
                src={whatsappIcon}
                alt="WhatsApp"
                className="w-3.5 h-3.5 object-contain flex-shrink-0 group-hover:scale-110 transition-transform"
              />
              <span className="group-hover:text-emerald-300 transition-colors">WhatsApp</span>
            </a>

            {/* Email Butonu */}
            <a
              href="mailto:info@we24agency.com"
              aria-label="E-posta: info@we24agency.com"
              onClick={() => trackEvent('email_click', { placement: 'navbar_top' })}
              className="flex items-center space-x-1.5 px-2.5 py-1 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 rounded-md font-semibold text-zinc-200 transition-all shadow-sm group text-[11px] sm:text-xs"
            >
              <Mail className="w-3.5 h-3.5 text-[#ff7a63] group-hover:scale-110 transition-transform shrink-0" />
              <span className="hidden sm:inline group-hover:text-white transition-colors">Email</span>
            </a>

            {/* TR / EN Dil Seçimi */}
            <div className="flex items-center space-x-0.5 pl-2 border-l border-white/15 font-bold text-[11px]">
              <button
                type="button"
                onClick={() => handleLanguageSwitch('TR')}
                className={`px-2 py-0.5 rounded text-[10px] sm:text-[11px] font-bold transition-all ${
                  language === 'TR'
                    ? 'bg-white text-zinc-950 shadow-sm scale-105'
                    : 'text-zinc-400 hover:text-white'
                }`}
                aria-label="TR - Türkçe"
              >
                TR
              </button>
              <button
                type="button"
                onClick={() => handleLanguageSwitch('EN')}
                className={`px-2 py-0.5 rounded text-[10px] sm:text-[11px] font-bold transition-all ${
                  language === 'EN'
                    ? 'bg-white text-zinc-950 shadow-sm scale-105'
                    : 'text-zinc-400 hover:text-white'
                }`}
                aria-label="EN - English"
              >
                EN
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Ana Navigasyon Çubuğu */}
      <nav
        className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur-md border-b border-border/40 py-3 shadow-2xl'
            : 'bg-background/60 backdrop-blur-sm py-3.5 border-b border-white/[0.04]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo (Beyaz Crop Logo) */}
          <Link to={language === 'TR' ? "/" : "/en"} className="flex items-center group py-0.5" aria-label="WE24 Agency Ana Sayfa">
            <img
              src={logoWhite}
              alt="WE24 Agency"
              className="h-7 sm:h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_2px_12px_rgba(213,80,59,0.2)]"
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-7">
            {navLinks.map((link) => {
              const isServices = link.hasDropdown;
              const isActive = isServices
                ? location.pathname.startsWith('/hizmetler') || location.pathname.startsWith('/en/services')
                : location.pathname === link.path;
              
              if (link.hasDropdown) {
                return (
                  <div
                    key={link.path}
                    className="relative"
                    onMouseEnter={handleMegaMouseEnter}
                    onMouseLeave={handleMegaMouseLeave}
                  >
                    <Link
                      to={link.path}
                      className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-white inline-flex items-center gap-1 py-2 ${
                        isActive ? 'text-white font-semibold' : 'text-muted-foreground'
                      }`}
                      aria-haspopup="true"
                      aria-expanded={isServicesMegaOpen}
                    >
                      <span>{link.name}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isServicesMegaOpen ? 'rotate-180 text-[#ff7a63]' : ''}`} />
                    </Link>

                    {/* Desktop Mega Menu Dropdown */}
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 w-[880px] max-w-[92vw] bg-[#05131f]/98 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl transition-all duration-300 z-50 ${
                        isServicesMegaOpen 
                          ? 'opacity-100 translate-y-1 visible pointer-events-auto' 
                          : 'opacity-0 translate-y-3 invisible pointer-events-none'
                      }`}
                    >
                      {/* Mega Menu Header */}
                      <div className="flex items-center justify-between border-b border-white/[0.08] pb-3 mb-5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs uppercase font-bold tracking-widest text-[#ff7a63]">
                            {language === 'TR' ? "UZMANLIK DİSİPLİNLERİMİZ" : "CORE DISCIPLINES"}
                          </span>
                          <span className="text-[11px] text-zinc-400">
                            ({currentServices.length} {language === 'TR' ? "Ana Kategori" : "Disciplines"})
                          </span>
                        </div>
                        <Link
                          to={language === 'TR' ? "/hizmetler" : "/en/services"}
                          onClick={() => setIsServicesMegaOpen(false)}
                          className="text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1 transition-colors"
                        >
                          <span>{language === 'TR' ? "Tüm Hizmetleri Gör" : "View All Services"}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-[#ff7a63]" />
                        </Link>
                      </div>

                      {/* Mega Menu Grid */}
                      <div className="grid grid-cols-3 gap-x-6 gap-y-5">
                        {currentServices.map((srv) => (
                          <div key={srv.id} className="group/item flex flex-col">
                            <Link
                              to={language === 'TR' ? `/hizmetler/${srv.slug}` : `/en/services/${srv.slug}`}
                              onClick={() => setIsServicesMegaOpen(false)}
                              className="flex items-start gap-2.5 p-2 rounded-xl hover:bg-white/[0.04] transition-all"
                            >
                              <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 mt-0.5 group-hover/item:border-[#ff7a63]/40 group-hover/item:bg-[#ff7a63]/10 transition-colors">
                                {renderServiceIcon(srv.icon)}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs font-bold text-white group-hover/item:text-[#ff7a63] transition-colors leading-snug block">
                                  {srv.title}
                                </span>
                                <p className="text-[11px] text-zinc-400 line-clamp-1 mt-0.5">
                                  {srv.shortDescription}
                                </p>
                              </div>
                            </Link>

                            {/* Sub-services Quick Pills */}
                            {srv.subserviceItems && srv.subserviceItems.length > 0 && (
                              <div className="pl-11 pr-2 flex flex-col gap-1 mt-1">
                                {srv.subserviceItems.slice(0, 4).map((sub) => (
                                  <Link
                                    key={sub.title}
                                    to={language === 'TR' ? `/hizmetler/${srv.slug}/${sub.slug}` : `/en/services/${srv.slug}/${sub.slug}`}
                                    onClick={() => setIsServicesMegaOpen(false)}
                                    className="text-[10px] text-zinc-400 hover:text-[#ff7a63] hover:translate-x-0.5 transition-all flex items-center gap-1.5 truncate"
                                  >
                                    <span className="w-1 h-1 rounded-full bg-zinc-600 group-hover/item:bg-[#ff7a63]" />
                                    <span className="truncate">{sub.title}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Mega Menu Footer Banner */}
                      <div className="border-t border-white/[0.08] pt-4 mt-5 flex items-center justify-between bg-white/[0.02] -mx-6 -mb-6 px-6 py-3 rounded-b-2xl">
                        <p className="text-[11px] text-zinc-400">
                          {language === 'TR' 
                            ? "Markanıza özel video prodüksiyon, reji veya yazılım teklifi almak için bizimle iletişime geçin." 
                            : "Contact us for custom video production, broadcasting, or software development proposals."}
                        </p>
                        <Link
                          to={language === 'TR' ? "/iletisim" : "/en/contact"}
                          onClick={() => setIsServicesMegaOpen(false)}
                          className="text-xs font-bold text-white bg-[#ff7a63] hover:bg-[#ff6247] px-3.5 py-1.5 rounded-full inline-flex items-center gap-1 transition-all shadow-md shrink-0"
                        >
                          <span>{language === 'TR' ? "Teklif Alın" : "Get a Quote"}</span>
                          <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium tracking-wide transition-colors duration-200 hover:text-white ${
                    isActive ? 'text-white font-semibold' : 'text-muted-foreground'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* Right Action: Teklif Al Butonu */}
          <div className="hidden lg:flex items-center">
            <Link
              to={language === 'TR' ? "/iletisim" : "/en/contact"}
              className="group relative inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white text-zinc-950 text-xs font-bold uppercase tracking-wider overflow-hidden shadow-lg hover:shadow-[#ff7a63]/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              <span className="relative z-10">{language === 'TR' ? "Projenizi Başlatalım" : "Start Your Project"}</span>
              <ArrowUpRight className="w-3.5 h-3.5 relative z-10 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              <div className="absolute inset-0 bg-[#ff7a63] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="absolute inset-0 z-10 flex items-center justify-center gap-2 text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {language === 'TR' ? "Projenizi Başlatalım" : "Start Your Project"} <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center lg:hidden">
            <button
              ref={menuTriggerRef}
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
              aria-expanded={isOpen}
              aria-label={isOpen ? "Menüyü Kapat" : "Menüyü Aç"}
            >
              {isOpen ? <X className="w-6 h-6 text-[#ff7a63]" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer (Gelişmiş Full-Screen Menü) */}
      <div
        className={`fixed inset-0 top-[88px] bg-[#030811]/98 backdrop-blur-2xl z-40 lg:hidden flex flex-col justify-between p-6 transition-all duration-300 overflow-y-auto ${
          isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="flex flex-col gap-4">
          {/* Mobil Menü Linkleri */}
          <div className="flex flex-col gap-2">
            {navLinks.map((link, idx) => {
              const isServices = link.hasDropdown;
              const isActive = isServices
                ? location.pathname.startsWith('/hizmetler') || location.pathname.startsWith('/en/services')
                : location.pathname === link.path;

              if (isServices) {
                return (
                  <div key={link.path} className="flex flex-col border-b border-white/[0.04] pb-2">
                    <div className="flex items-center justify-between">
                      <Link
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`text-xl font-display tracking-tight py-1 ${
                          isActive ? 'text-white font-bold underline underline-offset-8 decoration-[#ff7a63]' : 'text-muted-foreground'
                        }`}
                      >
                        {link.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                        className="p-2 text-zinc-400 hover:text-white"
                        aria-label="Hizmet kategorilerini aç"
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180 text-[#ff7a63]' : ''}`} />
                      </button>
                    </div>

                    {/* Mobile Services Accordion */}
                    {isMobileServicesOpen && (
                      <div className="flex flex-col gap-3 pt-3 pl-3 border-l-2 border-[#ff7a63]/40 mt-2 mb-1 animate-fade-in">
                        {currentServices.map((srv) => (
                          <div key={srv.id} className="flex flex-col gap-1">
                            <Link
                              to={language === 'TR' ? `/hizmetler/${srv.slug}` : `/en/services/${srv.slug}`}
                              onClick={() => setIsOpen(false)}
                              className="flex items-center gap-2 text-sm text-zinc-200 hover:text-white py-1 font-semibold"
                            >
                              <span className="shrink-0">{renderServiceIcon(srv.icon)}</span>
                              <span className="text-xs text-[#ff7a63]">{srv.title}</span>
                            </Link>

                            {/* Subservice links */}
                            {srv.subserviceItems && (
                              <div className="pl-6 flex flex-col gap-1.5 pb-1">
                                {srv.subserviceItems.map((sub) => (
                                  <Link
                                    key={sub.title}
                                    to={language === 'TR' ? `/hizmetler/${srv.slug}/${sub.slug}` : `/en/services/${srv.slug}/${sub.slug}`}
                                    onClick={() => setIsOpen(false)}
                                    className="text-[11px] text-zinc-400 hover:text-white transition-colors"
                                  >
                                    • {sub.title}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-xl font-display tracking-tight py-1 border-b border-white/[0.04] transition-transform ${
                    isOpen ? 'translate-x-0' : '-translate-x-4'
                  }`}
                  style={{ transitionDelay: `${idx * 25}ms` }}
                >
                  <span className={isActive ? 'text-foreground font-bold underline underline-offset-8 decoration-[#ff7a63]' : 'text-muted-foreground'}>
                    {link.name}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Mobile Footer Quick Actions & Contacts */}
          <div className="flex flex-col gap-4 border-t border-border/30 pt-5 mt-4">
            <div className="flex items-center justify-between pb-2 border-b border-white/[0.06]">
              <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wider">Dil / Language:</span>
              <div className="flex items-center space-x-1 font-bold text-xs">
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch('TR')}
                  className={`px-3 py-1 rounded transition-all ${
                    language === 'TR' ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-400 bg-white/5'
                  }`}
                  aria-label="TR - Türkçe"
                >
                  TR
                </button>
                <button
                  type="button"
                  onClick={() => handleLanguageSwitch('EN')}
                  className={`px-3 py-1 rounded transition-all ${
                    language === 'EN' ? 'bg-white text-zinc-950 shadow-sm' : 'text-zinc-400 bg-white/5'
                  }`}
                  aria-label="EN - English"
                >
                  EN
                </button>
              </div>
            </div>

            {/* Mobil Sosyal Medya */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            <div className="flex flex-col gap-2 text-sm text-zinc-300">
              <a
                href="tel:+905452292424"
                className="flex items-center gap-2.5 text-zinc-300 hover:text-white transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <Phone className="w-3 h-3 text-[#ff7a63]" />
                </div>
                <span className="text-xs font-semibold">+90 545 229 24 24</span>
              </a>

              <a
                href="mailto:info@we24agency.com"
                className="flex items-center gap-2.5 text-zinc-300 hover:text-white transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <Mail className="w-3 h-3 text-[#ff7a63]" />
                </div>
                <span className="text-xs font-semibold">info@we24agency.com</span>
              </a>

              <a
                href="https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-zinc-300 hover:text-white transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-3 h-3 text-[#ff7a63]" />
                </div>
                <span className="text-xs font-semibold">Nişantaşı, İstanbul</span>
              </a>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <a
                href="https://wa.me/905452292424"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase py-2.5 rounded-full shadow-lg transition-colors"
              >
                <img
                  src={whatsappIcon}
                  alt="WhatsApp"
                  className="w-3.5 h-3.5 object-contain flex-shrink-0"
                />
                <span>WhatsApp</span>
              </a>

              <Link
                to={language === 'TR' ? "/iletisim" : "/en/contact"}
                onClick={() => setIsOpen(false)}
                className="flex-1 text-center bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold uppercase py-2.5 rounded-full transition-colors"
              >
                {language === 'TR' ? "İletişime Geç" : "Contact"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
